import {CardElement, Elements} from '@stripe/react-stripe-js';
import {type Stripe, type StripeElements} from '@stripe/stripe-js';
import {type DeepPartial} from '@wandb/weave/common/types/base';
import {diffInDays} from '@wandb/weave/common/util/time';
import {Icon} from '@wandb/weave/components/Icon';
import {IconOnlyPill} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import _ from 'lodash';
import React, {useEffect, useMemo, useState} from 'react';

import {apolloClient as client} from '../../apolloClient';
import {
  type Account,
  AccountPlanType,
  AccountType,
} from '../../components/Search/SearchNav/types';
import {InstrumentedCenteredWaveLoader as Loader} from '../../components/utility/InstrumentedLoader';
import config, {envIsServerDeployment} from '../../config';
import {
  type AccountEnterpriseWeaveLimitsQuery,
  type AccountPrivilegesQuery,
  type Maybe,
  type Organization,
  type OrganizationSubscription,
  type OrganizationSubscriptionInfoQuery,
  OrganizationSubscriptionStatus,
  OrganizationSubscriptionType,
  OrgType,
  type Plan,
  PlanType,
  StorageUnit,
  type StripePaymentMethod,
  UsageType,
  useAccountEnterpriseWeaveLimitsQuery,
  useAccountPrivilegesQuery,
  useAccountSubscriptionPlansQuery,
  useSubscriptionPlansQuery,
  useUsageTabInfoQuery,
  type ViewerUserInfoQuery,
  type WeaveEnterpriseSubscription,
  WeaveIngestionLimitInMb,
  type WeaveLimits as WeaveLimitsRaw,
} from '../../generated/graphql';
import {VIEWER_USERINFO_QUERY} from '../../graphql/users';
import {captureError} from '../../integrations';
import {PlanName} from '../../pages/Billing/util';
import {type ExecutionResult} from '../../state/graphql/apolloWithAbort';
import {useViewer} from '../../state/viewer/hooks';
import {type Viewer} from '../../state/viewer/types';
import {endOfDayUTC} from '../date';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../errors';
import {
  ACCOUNT_ENTERPRISE_WEAVE_LIMITS,
  ACCOUNT_PRIVILEGES,
} from './pricing.query';

export const TRIAL_TEAM_SEATS = 100;

export const PLANS_WITH_COMPUTE_HOUR_LIMIT = [
  'basic',
  'standard_yearly',
  'standard_monthly',
  'starter_tier_1_monthly',
  'starter_tier_1_yearly',
  'starter_tier_2_monthly',
  'starter_tier_2_yearly',
  'starter_tier_3_monthly',
  'starter_tier_3_yearly',
];

const DEPRECATED_PLAN_NAMES = [PlanName.TrackingMonthly];

export const CONTACT_SALES_CLICKED_ANALYTICS = `Contact Sales Clicked`;

export type CheckoutPlan = {id: string; billingInterval: string; name: string};

export type OrganizationFlags = {
  noContact?: boolean;
  email_domain?: string;
  deployment_url?: string;
  deployment_contact_email?: string;
  default_entity_id_for_domain_matching?: string;
  coupon_id?: string;
  default_models_seat?: string;
  is_fmb?: boolean;
  is_legacy_personal_entity?: boolean;
  coreweave_organization_id?: string;
  sandbox_cluster_org_id?: string;
};

export type Org = {
  id: string;
  name: string;
  usedSeats: number;
  teams: Team[];
  subscriptions?: Subscription[];
  flags?: OrganizationFlags;
  createdAt?: string;
};

export function getAccountPlanTypeFromPrimarySub(
  subscription?: {
    plan: Pick<Plan, 'name'>;
    subscriptionType?: OrganizationSubscriptionType | undefined;
  } | null
): AccountPlanType {
  if (subscription == null) {
    return AccountPlanType.Personal;
  }

  if (isCommunityEditionPrimarySubscription(subscription)) {
    return AccountPlanType.Free;
  }
  if (isTrialPlanPrimarySubscription(subscription)) {
    return AccountPlanType.ThirtyDayTrial;
  }
  if (isTeamsPrimarySubscription(subscription)) {
    return AccountPlanType.Teams;
  }
  if (isProPrimarySubscription(subscription)) {
    return AccountPlanType.Pro;
  }
  if (isAcademicSubscription(subscription)) {
    return AccountPlanType.Academic;
  }
  if (isNonAcademicEnterprise(subscription)) {
    return AccountPlanType.Enterprise;
  }
  return AccountPlanType.Legacy;
}

export type Team = {id: string; name: string; computeHours?: number};

export type Subscription = {
  subscriptionType?: OrganizationSubscriptionType;
  status?: OrganizationSubscriptionStatus;
  expiresAt?: string | null;
  planType?: PlanType;
  seats?: number;
  plan?: Pick<Plan, 'id' | 'name' | 'planType'>;
  privileges?: {compute_hours?: number};
};

export type PlanClientData = {
  title: string;
  subtitle: string;
  features: string[];
  planType: 'free' | 'stripe' | 'enterprise';
  dbName: string | null;
};

export type PlanClientDataWithDBName = PlanClientData & {
  dbName: string;
};

export type PrimaryStripePlanInfo = Plan & {
  name: string;
  maxSeats: number;
  defaultPrivileges: {compute_hours?: number};
  displayName: string;
  billingInterval: string;
};

export function isPrimaryStripePlanInfo(
  plan: Pick<Plan, 'defaultPrivileges' | 'billingInterval'>
): plan is PrimaryStripePlanInfo {
  return plan.defaultPrivileges != null && plan.billingInterval != null;
}

export type PrimaryStripePlanInfoWithStripePrice = PrimaryStripePlanInfo & {
  stripePrice: {amount: number};
};

export function isPrimaryStripePlanInfoWithStripePrice(
  plan: Plan
): plan is PrimaryStripePlanInfoWithStripePrice {
  return isPrimaryStripePlanInfo(plan) && plan.stripePrice?.amount != null;
}

export type PrimaryStripePlanInfoWithUnitPrice = PrimaryStripePlanInfo & {
  unitPrice: number;
};

export function isPrimaryStripePlanInfoWithUnitPrice(
  plan: Pick<Plan, 'defaultPrivileges' | 'billingInterval' | 'unitPrice'>
): plan is PrimaryStripePlanInfoWithUnitPrice {
  return isPrimaryStripePlanInfo(plan) && plan.unitPrice != null;
}

export function convertToPrimaryStripePlanInfoWithUnitPrice(
  plan: PrimaryStripePlanInfoWithStripePrice
): PrimaryStripePlanInfoWithUnitPrice {
  return {
    ...plan,
    unitPrice: plan.stripePrice.amount,
  };
}

export type PrimarySubscriptionWithStripePlanInfo = OrganizationSubscription & {
  seats: number;
  plan: PrimaryStripePlanInfo;
};

export function isPrimarySubscriptionWithStripePlanInfo(
  subscription: NonNullable<
    OrganizationSubscriptionInfoQuery['organization']
  >['subscriptions'][number]
): subscription is PrimarySubscriptionWithStripePlanInfo {
  return isPrimaryStripePlanInfo(subscription.plan);
}

export type PlanInfo = PlanClientData & {
  monthlyPlan: Plan | null;
  yearlyPlan: Plan | null;
};
export type PlanInfoWithActualPlans = {
  enterprisePlan: PlanWithPrivileges;
};

export const STARTER_PLAN_TIER_1: PlanClientData = {
  planType: 'stripe',
  dbName: 'starter_tier_1',
  title: 'Upgrade to Starter Plan',
  subtitle: 'Annual Subscription',
  features: ['Feature 1', 'Feature 2', 'Feature 3'],
};

export const LEGACY_PLANS: PlanClientData[] = [
  {
    planType: 'free',
    dbName: null,
    title: 'Personal',
    subtitle: 'W&B basics for every practitioner',
    features: [
      'Unlimited public & private projects',
      'Public forum support',
      'Hyperparameter optimization tools',
    ],
  },
  {
    planType: 'stripe',
    dbName: 'startup',
    title: 'Startup',
    subtitle: 'Private collaboration on a small scale for pre-funded startups',
    features: [
      'Up to 3 users',
      '1 team',
      'Unlimited public & private projects',
      'Email support',
      'Hyperparameter optimization tools',
    ],
  },
  {
    planType: 'stripe',
    dbName: 'teams',
    title: 'Teams',
    subtitle: 'Essential management and security for small teams',
    features: [
      'Up to 5 users',
      '1 team',
      'Unlimited public & private projects',
      'Email support',
      'Hyperparameter optimization tools',
      'Service accounts',
    ],
  },
  {
    planType: 'enterprise',
    dbName: null,
    title: 'Enterprise',
    subtitle: 'Security, compliance, and flexible deployment',
    features: [
      'Unlimited private projects',
      'Single sign-on enabled',
      'Unlimited data storage',
      'Unlimited data retention',
      'Dedicated customer success',
      'Support & Service SLAs',
      'Professional services available',
      'On-Prem available',
      'Hyperparameter optimization tools',
    ],
  },
  {
    planType: 'stripe',
    dbName: 'standard',
    title: 'Upgrade to Starter Plan',
    subtitle: 'Starter plan',
    features: ['Feature 1', 'Feature 2', 'Feature 3'],
  },
];

type HasOrgType = {orgType: OrgType};
type HasStripeBillingInfoWithStatus = {
  stripeBillingInfo?: Maybe<{status: string}>;
};
export type HasSubsWithSubType = {
  subscriptions?: Array<{subscriptionType?: OrganizationSubscriptionType}>;
};
export type HasSubsWithPlanType = {
  subscriptions?: Array<{plan?: {planType?: PlanType}}>;
};
export type HasSubsWithSeatsType = {
  subscriptions?: Array<{seats: number}>;
};
export type HasSubsWithPlanName = {
  subscriptions?: Array<{plan?: {name?: string}}>;
};
export type HasSubsWithPlanBillingInterval = {
  subscriptions?: Array<{plan?: {billingInterval?: Maybe<string>}}>;
};
type HasSubsWithBillingPeriodAndPlanType = {
  subscriptions?: Array<{
    billingPeriodStart?: Maybe<string>;
    billingPeriodEnd?: Maybe<string>;
    plan?: {planType?: PlanType};
  }>;
};
type HasSubsWithPlanUnitPrice = {
  subscriptions?: Array<{plan?: {unitPrice?: Maybe<number>}}>;
};
type HasSubsWithSeats = {
  subscriptions?: Array<{seats?: number}>;
};
type HasMembers = {members?: Array<{role: string}>};
type HasPendingInvites = {pendingInvites?: {length: number}};

// this is not accurate anymore with pro plan
export function orgSeatsRemaining<
  T extends HasSubsWithPlanType &
    HasSubsWithSeats &
    HasMembers &
    HasPendingInvites,
>(org: T): number | null {
  const {subscriptions, members, pendingInvites} = org;
  if (subscriptions == null || members == null || pendingInvites == null) {
    return null;
  }

  const primarySub = getPrimarySub(org);
  const seatCount = primarySub?.seats;
  if (seatCount == null) {
    return null;
  }

  // TODO: This role field should have an enum typpe.
  const memberCount = members.filter(m => m?.role !== 'billing-only').length;
  const inviteCount = pendingInvites.length;

  return seatCount - (memberCount + inviteCount);
}

export function orgPrimarySubUnitPrice<
  T extends HasSubsWithPlanType & HasSubsWithPlanUnitPrice,
>(org: T): number | null {
  const primarySub = getPrimarySub(org);
  const unitPrice = primarySub?.plan?.unitPrice;
  if (unitPrice == null) {
    return null;
  }

  return unitPrice / 100;
}

export function orgPrimarySubBillingInterval<
  T extends HasSubsWithPlanType & HasSubsWithPlanBillingInterval,
>(org: T): string | null {
  const primarySub = getPrimarySub(org);
  return primarySub?.plan?.billingInterval ?? null;
}

export type SubscriptionForOrg<T extends {subscriptions?: Array<{}>}> =
  NonNullable<T['subscriptions']>[number];

export function getPrimarySub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(s => s?.plan?.planType === PlanType.Primary) ??
    null
  );
}

export function getModelSeatsSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(s => s?.plan?.planType === PlanType.ModelsSeats) ??
    null
  );
}

export function getSeatsSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return getModelSeatsSub(org) ?? getPrimarySub(org);
}

export function getModelSeatsCount<
  T extends HasSubsWithPlanType & HasSubsWithSeatsType,
>(org?: T): number {
  return getSeatsSub<T>(org)?.seats ?? 0;
}

export function getWeaveUsageSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(
      s => s?.plan?.planType === PlanType.WeaveIngestionUsage
    ) ?? null
  );
}

export function getWeaveCommitSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(
      s => s?.plan?.planType === PlanType.WeaveIngestionCommit
    ) ?? null
  );
}

export function getStorageUsageSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(s => s?.plan?.planType === PlanType.Storage) ??
    null
  );
}

export const weaveCommitPlanNameToWeaveIngestionLimit = (
  planName: string
): WeaveIngestionLimitInMb => {
  switch (planName) {
    case PlanName.WeaveAnnual1500:
    case PlanName.WeaveMonthly1500:
    case PlanName.WeaveUsage1500:
      return WeaveIngestionLimitInMb.Limit_1500;

    case PlanName.WeaveAnnual2000:
    case PlanName.WeaveMonthly2000:
    case PlanName.WeaveUsage2000:
      return WeaveIngestionLimitInMb.Limit_2000;

    case PlanName.WeaveAnnual3500:
    case PlanName.WeaveMonthly3500:
    case PlanName.WeaveUsage3500:
      return WeaveIngestionLimitInMb.Limit_3500;

    case PlanName.WeaveAnnual5000:
    case PlanName.WeaveMonthly5000:
    case PlanName.WeaveUsage5000:
      return WeaveIngestionLimitInMb.Limit_5000;

    case PlanName.WeaveAnnual6500:
    case PlanName.WeaveMonthly6500:
    case PlanName.WeaveUsage6500:
      return WeaveIngestionLimitInMb.Limit_6500;

    case PlanName.WeaveAnnual8000:
    case PlanName.WeaveMonthly8000:
    case PlanName.WeaveUsage8000:
      return WeaveIngestionLimitInMb.Limit_8000;

    case PlanName.WeaveAnnual10000:
    case PlanName.WeaveMonthly10000:
    case PlanName.WeaveUsage10000:
      return WeaveIngestionLimitInMb.Limit_10000;

    case PlanName.WeaveAnnual12500:
    case PlanName.WeaveMonthly12500:
    case PlanName.WeaveUsage12500:
      return WeaveIngestionLimitInMb.Limit_12500;

    case PlanName.WeaveAnnual15000:
    case PlanName.WeaveMonthly15000:
    case PlanName.WeaveUsage15000:
      return WeaveIngestionLimitInMb.Limit_15000;

    case PlanName.WeaveAnnual20000:
    case PlanName.WeaveMonthly20000:
    case PlanName.WeaveUsage20000:
      return WeaveIngestionLimitInMb.Limit_20000;

    case PlanName.WeaveAnnual25000:
    case PlanName.WeaveMonthly25000:
    case PlanName.WeaveUsage25000:
      return WeaveIngestionLimitInMb.Limit_25000;

    case PlanName.WeaveAnnual30000:
    case PlanName.WeaveMonthly30000:
    case PlanName.WeaveUsage30000:
      return WeaveIngestionLimitInMb.Limit_30000;

    case PlanName.WeaveAnnual35000:
    case PlanName.WeaveMonthly35000:
    case PlanName.WeaveUsage35000:
      return WeaveIngestionLimitInMb.Limit_35000;

    default:
      return WeaveIngestionLimitInMb.Limit_1500;
  }
};

export function getTrackedHourUsageSub<T extends HasSubsWithPlanType>(
  org?: T
): SubscriptionForOrg<T> | null {
  return (
    org?.subscriptions?.find(s => s?.plan?.planType === PlanType.HourUsage) ??
    null
  );
}

export function getStorageSub<T extends HasSubsWithPlanName>(
  organization?: T
): SubscriptionForOrg<T> | undefined {
  return (
    organization?.subscriptions?.find(
      sub =>
        sub.plan?.name === PlanName.MonthlyStorage2023 ||
        sub.plan?.name === PlanName.MonthlyStorage200GB
    ) ??
    organization?.subscriptions?.find(
      sub => sub.plan?.name === PlanName.StorageMonthly
    )
  );
}

// TODO(hhuang): Just propagate plan type and call it a day!!!
export function getInferenceSub<T extends HasSubsWithPlanName>(
  organization?: T
): SubscriptionForOrg<T> | undefined {
  return organization?.subscriptions?.find(
    sub =>
      sub.plan?.name === PlanName.InferenceUsage500 ||
      sub.plan?.name === PlanName.InferenceUsage200
  );
}

// TODO(dha-beep): Propagate plan type instead of searching for the plan name (same problem as above getInferenceSub)
export function getWbAgentSub<T extends HasSubsWithPlanName>(
  organization?: T
): SubscriptionForOrg<T> | undefined {
  return organization?.subscriptions?.find(
    sub => sub.plan?.name === PlanName.WbAgentUsage1000
  );
}

export function getSandboxesSub<T extends HasSubsWithPlanName>(
  organization?: T
): SubscriptionForOrg<T> | undefined {
  return organization?.subscriptions?.find(
    sub =>
      sub.plan?.name === PlanName.SandboxesUsage0 ||
      sub.plan?.name === PlanName.SandboxesUsage3000
  );
}

export type BillingPeriod = Pick<
  WeaveEnterpriseSubscription,
  'billingPeriodStart' | 'billingPeriodEnd'
>;
type HasWeaveEnterpriseSubscription = {
  weaveEnterpriseSubscription?: BillingPeriod | null;
};

export function getEnterpriseWeaveOrbSubBillingPeriod<
  T extends HasWeaveEnterpriseSubscription & HasSubsWithPlanType,
>(organization?: T): BillingPeriod | undefined | null {
  return organization?.weaveEnterpriseSubscription;
}

function getBillingPeriodForPlanType<
  T extends HasSubsWithBillingPeriodAndPlanType,
>(planType: PlanType, organization?: T): BillingPeriod | undefined {
  const sub = organization?.subscriptions?.find(
    sub => sub?.plan?.planType === planType
  );
  if (sub?.billingPeriodStart == null || sub?.billingPeriodEnd == null) {
    return undefined;
  }
  return {
    billingPeriodStart: sub.billingPeriodStart,
    billingPeriodEnd: sub.billingPeriodEnd,
  };
}

/** Pro billing period from the storage subscription, matching SKU GetBillingPeriod. */
export function getStorageSubBillingPeriod<
  T extends HasSubsWithBillingPeriodAndPlanType & HasSubsWithPlanName,
>(organization?: T): SubscriptionForOrg<T> | undefined {
  return getStorageSub(organization);
}

/** Training billing period, matching SKU GetBillingPeriod. */
export function getTrainingSubBillingPeriod<
  T extends HasSubsWithBillingPeriodAndPlanType & HasSubsWithPlanName,
>(
  accountPlanType: AccountPlanType | undefined,
  organization?: T
): BillingPeriod | undefined {
  switch (accountPlanType) {
    case AccountPlanType.Pro:
      // TODO: Use the training product subscription once training billing is integrated.
      return getStorageSubBillingPeriod(organization);
    case AccountPlanType.Free:
    case AccountPlanType.Academic:
      // TODO: Use getBillingPeriodForPlanType(PlanType.TrainingUsage, organization)
      // once training billing is exposed in the GraphQL PlanType enum.
      return undefined;
    default:
      return undefined;
  }
}

/** WB Agent billing period, matching SKU GetBillingPeriod. */
export function getWbagentSubBillingPeriod<
  T extends HasSubsWithBillingPeriodAndPlanType & HasSubsWithPlanName,
>(
  accountPlanType: AccountPlanType | undefined,
  organization?: T
): SubscriptionForOrg<T> | undefined {
  switch (accountPlanType) {
    case AccountPlanType.Pro:
      // TODO: Use the wb-agent product subscription once wb-agent billing is integrated.
      return getStorageSubBillingPeriod(organization);
    default:
      return undefined;
  }
}

export function getSandboxesSubBillingPeriod<
  T extends HasSubsWithBillingPeriodAndPlanType,
>(
  accountPlanType: AccountPlanType | undefined,
  organization?: T
): BillingPeriod | undefined {
  switch (accountPlanType) {
    case AccountPlanType.Pro:
    case AccountPlanType.Free:
    case AccountPlanType.Academic:
      return (
        getBillingPeriodForPlanType(PlanType.SandboxesUsage, organization) ??
        getStorageSubBillingPeriod(organization)
      );
    default:
      return undefined;
  }
}

export function isNonAcademicEnterprise<
  T extends HasSubsWithPlanName & HasSubsWithSubType,
>(primarySub: SubscriptionForOrg<T>): boolean {
  return (
    primarySub?.plan?.name === PlanName.Enterprise &&
    !isAcademicSubscription(primarySub)
  );
}

function getWeaveSubBillingPeriod<
  T extends HasWeaveEnterpriseSubscription & HasSubsWithPlanType,
>(
  planType: PlanType,
  privileges: Privileges,
  organization?: T
): BillingPeriod | undefined {
  const primarySub = getPrimarySub(organization);
  const isEnterprise =
    primarySub != null && isNonAcademicEnterprise(primarySub);
  if (isEnterprise) {
    const enterpriseOrbSubscription =
      getEnterpriseWeaveOrbSubBillingPeriod(organization);
    if (enterpriseOrbSubscription == null) {
      // If it's an enterprise without an orb subscription, then they are under a Weave trial - read the dates from the privileges.
      return {
        billingPeriodStart:
          privileges.weaveLimitDateInterval.start.toISOString(),
        billingPeriodEnd: privileges.weaveLimitDateInterval.end.toISOString(),
      };
    }
    // If it's an enterprise with an orb subscription, then just use the orb subscription.
    return enterpriseOrbSubscription;
  }
  // If it's not enterprise, then use the stripe sub for that plan type.
  return getBillingPeriodForPlanType(planType, organization);
}

function getInferenceSubBillingPeriod<T extends HasSubsWithPlanType>(
  planType: PlanType,
  privileges: Privileges,
  organization?: T
): BillingPeriod | undefined {
  const primarySub = getPrimarySub(organization);
  const isEnterprise =
    primarySub != null && isNonAcademicEnterprise(primarySub);
  if (isEnterprise) {
    const enterpriseOrbSubscription = null; // TODO: Implement this once we do orb
    // getEnterpriseWeaveOrbSubBillingPeriod(organization);
    if (enterpriseOrbSubscription == null) {
      // If it's an enterprise without an orb subscription, then they are under a Weave trial - read the dates from the privileges.
      return {
        billingPeriodStart:
          privileges.inferenceLimitDateInterval.start.toISOString(),
        billingPeriodEnd:
          privileges.inferenceLimitDateInterval.end.toISOString(),
      };
    }
    // If it's an enterprise with an orb subscription, then just use the orb subscription.
    return enterpriseOrbSubscription;
  }
  // If it's not enterprise, then use the stripe sub for that plan type.
  return getBillingPeriodForPlanType(planType, organization);
}

export function getInferenceOverageSubBillingPeriod<
  T extends HasSubsWithPlanType & HasSubsWithPlanName,
>(privileges: Privileges, organization?: T): BillingPeriod | undefined {
  return getInferenceSubBillingPeriod(
    PlanType.Storage, // placeholder until we get real inference overage plan
    privileges,
    organization
  );
}

export function getWeaveOverageSubBillingPeriod<
  T extends HasWeaveEnterpriseSubscription &
    HasSubsWithPlanType &
    HasSubsWithPlanName,
>(privileges: Privileges, organization?: T): BillingPeriod | undefined {
  return getWeaveSubBillingPeriod(
    PlanType.WeaveIngestionUsage,
    privileges,
    organization
  );
}

export function getWeaveCommitSubBillingPeriod<
  T extends HasWeaveEnterpriseSubscription &
    HasSubsWithPlanType &
    HasSubsWithPlanName,
>(privileges: Privileges, organization?: T): BillingPeriod | undefined {
  return getWeaveSubBillingPeriod(
    PlanType.WeaveIngestionCommit,
    privileges,
    organization
  );
}

export function getTrackedHoursSub<T extends HasSubsWithPlanType>(
  organization?: T
): SubscriptionForOrg<T> | null {
  return (
    organization?.subscriptions?.find(
      s => s?.plan?.planType === PlanType.HourOverage
    ) ?? null
  );
}

export function getReferenceSub<T extends HasSubsWithPlanName>(
  organization?: T
): SubscriptionForOrg<T> | undefined {
  return organization?.subscriptions?.find(
    sub => sub.plan?.name === PlanName.TrackingMonthly
  );
}

export function getPrimaryPaymentMethod(
  paymentMethods?: StripePaymentMethod[]
): StripePaymentMethod | undefined {
  return paymentMethods?.find(pm => pm.isDefault);
}

export function isDeprecatedSubscription(
  subscription: DeepPartial<OrganizationSubscription>
): boolean {
  return DEPRECATED_PLAN_NAMES.some(
    planName => subscription.plan?.name === planName
  );
}

export function isDisabledSubscription(
  subscription: DeepPartial<OrganizationSubscription>
): boolean {
  return subscription.status === OrganizationSubscriptionStatus.Disabled;
}

export function isExpiredSubscription(
  subscription: Pick<OrganizationSubscription, 'expiresAt'>
): boolean {
  return (
    subscription.expiresAt != null &&
    new Date(subscription.expiresAt) <= new Date()
  );
}

type ExpiringSubscriptionResult = {
  isExpiring: boolean;
  daysUntilExpire?: number;
};

export function isExpiringSubscription(
  subscription: Pick<OrganizationSubscription, 'expiresAt'>
): ExpiringSubscriptionResult {
  if (subscription.expiresAt == null) {
    return {isExpiring: false};
  }

  const expiresAtDate = new Date(subscription.expiresAt);
  const isExpiring = new Date() <= expiresAtDate;
  const daysUntilExpire = isExpiring
    ? diffInDays(expiresAtDate, new Date())
    : undefined;

  return {isExpiring, daysUntilExpire};
}

export function isTeamsPrimaryPlan(plan: Pick<Plan, 'name'>): boolean {
  return (
    plan.name === PlanName.YearlyTeams2023 ||
    plan.name === PlanName.MonthlyTeams2023
  );
}

export function isProPrimaryPlan(plan: Pick<Plan, 'name'>): boolean {
  return plan.name === PlanName.ProAnnual || plan.name === PlanName.ProMonthly;
}

export function isCommunityEditionPrimaryPlan(
  plan: Pick<Plan, 'name'>
): boolean {
  return plan.name === PlanName.CommunityEdition;
}

export function isTrialPlanPrimaryPlan(plan: Pick<Plan, 'name'>): boolean {
  return plan.name === PlanName.TrialPlan;
}

export function isTeamsPrimarySubscription(subscription: {
  plan: Pick<Plan, 'name'>;
}): boolean {
  return isTeamsPrimaryPlan(subscription.plan);
}

export function isProPrimarySubscription(subscription: {
  plan: Pick<Plan, 'name'>;
}): boolean {
  return isProPrimaryPlan(subscription.plan);
}

export function isCommunityEditionPrimarySubscription(subscription: {
  plan: Pick<Plan, 'name'>;
}): boolean {
  return isCommunityEditionPrimaryPlan(subscription.plan);
}

export function isTrialPlanPrimarySubscription(subscription: {
  plan: Pick<Plan, 'name'>;
}): boolean {
  return isTrialPlanPrimaryPlan(subscription.plan);
}

export function shouldAllowStandaloneStoragePurchase(account: Account) {
  return (
    account.accountType === AccountType.Personal ||
    account.accountType === AccountType.Academic
  );
}

export function shouldEnforceTrackingHour(subscription: {
  plan: Pick<Plan, 'name'>;
}): boolean {
  return !(
    subscription.plan.name.includes('tier_1') ||
    subscription.plan.name.includes('tier_2') ||
    subscription.plan.name.includes('tier_3')
  );
}

// all the subscription types except academic and academic trial
// should be enforced for the expired subscription nudge bar & enforcement
// paid self-service subscriptions are gated to not set expiration date in backend
// so technically manual trial, user led trial and enterprise gets enforced
export function shouldEnforceExpiration(
  subscription: DeepPartial<OrganizationSubscription>
): boolean {
  return !isAcademicSubscription(subscription);
}

export function isAcademicSubscription<T extends HasSubsWithSubType>(
  subscription: SubscriptionForOrg<T>
): boolean {
  return (
    subscription.subscriptionType === OrganizationSubscriptionType.Academic ||
    subscription.subscriptionType === OrganizationSubscriptionType.AcademicTrial
  );
}

export const StripeElementsComp: React.FC<{
  children?: React.ReactNode;
  hideLoading?: boolean;
}> = React.memo(({children, hideLoading}) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);

  useEffect(() => {
    if (config.ENVIRONMENT_IS_PRIVATE || config.ENVIRONMENT_NAME === 'test') {
      return;
    }
    const stripeApiKey = config.STRIPE_API_KEY;
    if (stripeApiKey == null) {
      throw new Error('Stripe API key not set!');
    }

    import('@stripe/stripe-js').then(async module => {
      const stripeLoadResult = await module.loadStripe(stripeApiKey);
      setStripe(stripeLoadResult);
    });
  }, []);

  if (stripe == null && !hideLoading) {
    return <Loader name="stripe-elements" size="huge" />;
  }

  // Always render children inside <Elements>, even while stripe is still null.
  // <Elements stripe={null}> is the documented async-loading pattern; returning
  // a bare fragment first and re-parenting under <Elements> once stripe loads
  // unmounts and remounts the entire child tree.
  return <Elements stripe={stripe}>{children}</Elements>;
});

export const PremiumFeatureToolTipText = ({
  isPremiumFeatureDisabled,
}: {
  isPremiumFeatureDisabled: boolean;
}) => (
  <Tailwind>
    <div className="flex items-center">
      <Icon name="crown-pro" className="mr-5" />
      {isPremiumFeatureDisabled
        ? 'Upgrade to enable this feature'
        : 'Premium feature enabled'}
    </div>
  </Tailwind>
);

export const PremiumFeatureTooltip = ({
  isPremiumFeatureDisabled,
}: {
  isPremiumFeatureDisabled: boolean;
}) => {
  if (!isPremiumFeatureDisabled) {
    return null; //WB-25466 Only show Premium Feature Icon to non-paying customers
  }

  return (
    <Tooltip
      position="top center"
      trigger={
        <div className="ml-5">
          <IconOnlyPill
            color="purple"
            icon="crown-pro"
            style={{flexShrink: 1}}
          />
        </div>
      }>
      <PremiumFeatureToolTipText isPremiumFeatureDisabled={true} />
    </Tooltip>
  );
};

type SubscriptionPlansResult = {
  loading: boolean;
  planInfo: PlanInfoWithActualPlans | null;
  storagePlanID: string | null;
  trackingPlanID: string | null;
};

export type PlanWithPrivileges = Omit<Plan, 'defaultPrivileges'> & {
  defaultPrivileges: Privileges;
};

interface UseSubscriptionPlansOptions {
  skip?: boolean;
}

export function useGetSubscriptionPlanByName({
  skip = false,
}: UseSubscriptionPlansOptions = {}): {
  loading: boolean;
  plans: {[key in PlanName]?: PlanWithPrivileges};
} {
  const {loading, data} = useSubscriptionPlansQuery({
    context: propagateErrorsContext(),
    skip,
  });

  const plans: {[key in PlanName]?: PlanWithPrivileges} = useMemo(() => {
    return loading === true
      ? {}
      : Object.fromEntries(
          data?.plans?.reduce(
            (entries: Array<[PlanName, PlanWithPrivileges]>, plan) => {
              if (plan == null) {
                return entries;
              }
              const defaultPrivileges = getPlanPrivileges(plan);
              const planWithPrivileges: PlanWithPrivileges = {
                ...plan,
                defaultPrivileges,
              };
              if (!Object.values(PlanName).includes(plan.name as PlanName)) {
                // The server plans might be out of sync with the PlanName enum on the frontend.
                // Don't include any plans that can't be accessed via the enum.
                return entries;
              }
              const entry: [PlanName, PlanWithPrivileges] = [
                plan.name as PlanName,
                planWithPrivileges,
              ];
              entries.push(entry);
              return entries;
            },
            []
          ) ?? []
        );
  }, [data?.plans, loading]);

  return {loading, plans};
}

export function useGetMinimumModelsSeatsToPurchase(
  orgName: string,
  currentFullSeats: number
): {
  minModelsSeatsToPurchase: number | null;
  isWeaveOnlyUser: boolean | null;
  loading: boolean;
} {
  // Use a very wide date range to get all-time tracked hours usage
  const veryEarlyDate = '2000-01-01T00:00:00.000Z';
  const today = endOfDayUTC(new Date()).toISOString();

  const {data: trackedHoursComputeSecondsData, loading: trackedHoursLoading} =
    useUsageTabInfoQuery({
      variables: {
        orgName,
        usageType: UsageType.TrackedHours,
        intervalMarkers: [veryEarlyDate, today],
      },
    });

  const usage = trackedHoursComputeSecondsData?.organization?.usage?.reduce(
    (total, {value}) => total + value,
    0
  );

  if (trackedHoursLoading) {
    return {
      minModelsSeatsToPurchase: null,
      isWeaveOnlyUser: null,
      loading: trackedHoursLoading,
    };
  }

  // Customer who ONLY use Weave, will not have any usage in form of tracked hours
  if (usage === 0) {
    return {
      minModelsSeatsToPurchase: 1,
      isWeaveOnlyUser: true,
      loading: trackedHoursLoading,
    };
  }

  return {
    minModelsSeatsToPurchase: Math.max(currentFullSeats, 1),
    isWeaveOnlyUser: false,
    loading: trackedHoursLoading,
  };
}

export function useSubscriptionPlans(
  options: UseSubscriptionPlansOptions = {}
): SubscriptionPlansResult {
  const {loading, plans} = useGetSubscriptionPlanByName(options);

  const planInfo = useMemo(() => {
    const enterprisePlan = plans[PlanName.Enterprise];
    if (enterprisePlan == null) {
      return null;
    }
    return {
      enterprisePlan,
    };
  }, [plans]);

  const storagePlanID = useMemo(
    () => plans[PlanName.StorageMonthly]?.id ?? null,
    [plans]
  );

  const trackingPlanID = useMemo(
    () => plans[PlanName.TrackingMonthly]?.id ?? null,
    [plans]
  );

  return {loading, planInfo, storagePlanID, trackingPlanID};
}

export function useIsServiceAccountsEnabled(account: Account | null): boolean {
  const {privileges} = useAccountPrivileges(account);

  if (envIsServerDeployment) {
    return true;
  }

  return privileges?.serviceAccountsEnabled;
}

export type WeaveLimits = {
  weaveLimitBytes: number;
  weaveOverageCostCents: number;
  weaveOverageUnit: StorageUnit;
};

export type LimitDateInterval = {
  start: Date;
  end: Date;
};

export type InferenceLimits = {
  inferenceSafetyLimit: number;
  inferenceFreeLimit: number;
  enforceInferenceFreeLimit: boolean;
};

export type TrainingLimits = {
  trainingFreeLimit: number;
  enforceTrainingFreeLimit: boolean;
};

export type SandboxesLimits = {
  sandboxesFreeLimit: number;
  sandboxesSafetyLimit: number;
  enforceSandboxesFreeLimit: boolean;
};

export type WbagentLimits = {
  wbagentFreeLimit: number;
  wbagentSafetyLimit: number;
  enforceWbagentFreeLimit: boolean;
};

export type Privileges = {
  computeHours: number;
  numPrivateTeam: number;
  orgDashEnabled: boolean;
  storageLimitGB: number;
  roleManagementEnabled: boolean;
  serviceAccountsEnabled: boolean;
  referenceLimitGB: number;
  numViewOnlySeats: number;
  computeHoursMonthlyLimit: number;
  enforceModelsSeatLimit: boolean;
  weaveLimits?: WeaveLimits | null;
  inferenceLimits: InferenceLimits;
  trainingLimits: TrainingLimits;
  sandboxesLimits: SandboxesLimits;
  wbagentLimits: WbagentLimits;
  weaveLimitDateInterval: LimitDateInterval;
  inferenceLimitDateInterval: LimitDateInterval;
  trainingLimitDateInterval: LimitDateInterval;
  sandboxesLimitDateInterval: LimitDateInterval;
  wbagentLimitDateInterval: LimitDateInterval;
  workspaceTemplatesEnabled: boolean;
  showMemberActivity: boolean;
};

// TODO(hhuang): rework graphql schema to have explicit types for privileges so we don't need this

// DO NOT RELY on this default configuration, please define expected defaults in subscriptions.go instead
// so it will be available in both frontend and backend, this is only so we can force these values to
// be some non-null-undefined value in our code
export const DEFAULT_PRIVILEGES: Privileges = {
  computeHoursMonthlyLimit: 0,
  numPrivateTeam: 1,
  orgDashEnabled: false,
  storageLimitGB: 100,
  roleManagementEnabled: true,
  serviceAccountsEnabled: false,
  referenceLimitGB: 100,
  numViewOnlySeats: 0,
  enforceModelsSeatLimit: false,
  weaveLimitDateInterval: {
    start: new Date(),
    end: new Date(),
  },
  inferenceLimits: {
    inferenceSafetyLimit: 0,
    inferenceFreeLimit: 0,
    enforceInferenceFreeLimit: true,
  },
  inferenceLimitDateInterval: {
    start: new Date(),
    end: new Date(),
  },
  trainingLimitDateInterval: {
    start: new Date(),
    end: new Date(),
  },
  trainingLimits: {
    trainingFreeLimit: 0,
    enforceTrainingFreeLimit: true,
  },
  sandboxesLimits: {
    sandboxesFreeLimit: 0,
    sandboxesSafetyLimit: 0,
    enforceSandboxesFreeLimit: true,
  },
  sandboxesLimitDateInterval: {
    start: new Date(),
    end: new Date(),
  },
  wbagentLimits: {
    wbagentFreeLimit: 0,
    wbagentSafetyLimit: 0,
    enforceWbagentFreeLimit: true,
  },
  wbagentLimitDateInterval: {
    start: new Date(),
    end: new Date(),
  },
  computeHours: 0,
  workspaceTemplatesEnabled: false,
  showMemberActivity: false,
};

// TODO(hhuang): Update schema graphql to actually list individual fields, and then update this type
// to use the raw graphql type.
type RawPrivileges = any;

export function rawPrivilegesToAccountPrivileges(
  privileges: RawPrivileges
): Privileges {
  // spread operator doesn't work if incoming object has explicit undefined values help
  return {
    computeHours: privileges?.compute_hours ?? DEFAULT_PRIVILEGES.computeHours,
    computeHoursMonthlyLimit:
      privileges?.compute_hours_monthly_limit ??
      DEFAULT_PRIVILEGES.computeHoursMonthlyLimit,
    numPrivateTeam:
      privileges?.num_private_teams ?? DEFAULT_PRIVILEGES.numPrivateTeam,
    orgDashEnabled:
      privileges?.org_dash_enabled ?? DEFAULT_PRIVILEGES.orgDashEnabled,
    storageLimitGB:
      privileges?.storage_gigs ?? DEFAULT_PRIVILEGES.storageLimitGB,
    roleManagementEnabled:
      privileges?.role_management_enabled ??
      DEFAULT_PRIVILEGES.roleManagementEnabled,
    serviceAccountsEnabled:
      privileges?.service_accounts_enabled ??
      DEFAULT_PRIVILEGES.serviceAccountsEnabled,
    referenceLimitGB:
      privileges?.reference_limit_gb ?? DEFAULT_PRIVILEGES.referenceLimitGB,
    numViewOnlySeats:
      privileges?.num_viewonly_seats ?? DEFAULT_PRIVILEGES.numViewOnlySeats,
    enforceModelsSeatLimit:
      privileges?.enforce_models_seat_limit ??
      DEFAULT_PRIVILEGES.enforceModelsSeatLimit,
    weaveLimits: privileges?.weave_limits,
    weaveLimitDateInterval: privileges?.weave_limit_date_interval
      ? {
          start: new Date(privileges?.weave_limit_date_interval.start),
          end: new Date(privileges?.weave_limit_date_interval.end),
        }
      : DEFAULT_PRIVILEGES.weaveLimitDateInterval,
    inferenceLimits: {
      inferenceSafetyLimit:
        privileges?.inference_safety_limit ??
        DEFAULT_PRIVILEGES.inferenceLimits.inferenceSafetyLimit,
      inferenceFreeLimit:
        privileges?.inference_free_limit ??
        DEFAULT_PRIVILEGES.inferenceLimits.inferenceFreeLimit,
      enforceInferenceFreeLimit:
        privileges?.enforce_inference_free_limit ??
        DEFAULT_PRIVILEGES.inferenceLimits.enforceInferenceFreeLimit,
    },
    inferenceLimitDateInterval: privileges?.inference_limit_date_interval
      ? {
          start: new Date(privileges?.inference_limit_date_interval.start),
          end: new Date(privileges?.inference_limit_date_interval.end),
        }
      : DEFAULT_PRIVILEGES.inferenceLimitDateInterval,
    trainingLimits: {
      trainingFreeLimit:
        privileges?.training_free_limit ??
        DEFAULT_PRIVILEGES.trainingLimits.trainingFreeLimit,
      enforceTrainingFreeLimit:
        privileges?.enforce_training_free_limit ??
        DEFAULT_PRIVILEGES.trainingLimits.enforceTrainingFreeLimit,
    },
    trainingLimitDateInterval: privileges?.training_limit_date_interval
      ? {
          start: new Date(privileges?.training_limit_date_interval.start),
          end: new Date(privileges?.training_limit_date_interval.end),
        }
      : DEFAULT_PRIVILEGES.trainingLimitDateInterval,
    sandboxesLimits: {
      sandboxesFreeLimit:
        privileges?.sandboxes_free_limit ??
        DEFAULT_PRIVILEGES.sandboxesLimits.sandboxesFreeLimit,
      sandboxesSafetyLimit:
        privileges?.sandboxes_safety_limit ??
        DEFAULT_PRIVILEGES.sandboxesLimits.sandboxesSafetyLimit,
      enforceSandboxesFreeLimit:
        privileges?.enforce_sandboxes_free_limit ??
        DEFAULT_PRIVILEGES.sandboxesLimits.enforceSandboxesFreeLimit,
    },
    sandboxesLimitDateInterval: privileges?.sandboxes_limit_date_interval
      ? {
          start: new Date(privileges?.sandboxes_limit_date_interval.start),
          end: new Date(privileges?.sandboxes_limit_date_interval.end),
        }
      : DEFAULT_PRIVILEGES.sandboxesLimitDateInterval,
    wbagentLimits: {
      wbagentFreeLimit:
        privileges?.wbagent_free_limit ??
        DEFAULT_PRIVILEGES.wbagentLimits.wbagentFreeLimit,
      wbagentSafetyLimit:
        privileges?.wbagent_safety_limit ??
        DEFAULT_PRIVILEGES.wbagentLimits.wbagentSafetyLimit,
      enforceWbagentFreeLimit:
        privileges?.enforce_wbagent_free_limit ??
        DEFAULT_PRIVILEGES.wbagentLimits.enforceWbagentFreeLimit,
    },
    wbagentLimitDateInterval: privileges?.wbagent_limit_date_interval
      ? {
          start: new Date(privileges?.wbagent_limit_date_interval.start),
          end: new Date(privileges?.wbagent_limit_date_interval.end),
        }
      : DEFAULT_PRIVILEGES.wbagentLimitDateInterval,
    workspaceTemplatesEnabled:
      privileges?.workspace_templates_enabled ??
      DEFAULT_PRIVILEGES.workspaceTemplatesEnabled,
    showMemberActivity:
      privileges?.show_member_activity ?? DEFAULT_PRIVILEGES.showMemberActivity,
  };
}

export function useAccountPrivileges(account: Account | null) {
  const skip =
    account == null ||
    account.accountType === AccountType.Personal ||
    envIsServerDeployment;

  const {loading, data, error, refetch} = useAccountPrivilegesQuery({
    variables: {organizationId: account?.id ?? ''},
    skip,
  });

  if (skip || loading || data?.organization == null) {
    return {
      loading,
      privileges: DEFAULT_PRIVILEGES,
      refetch,
    };
  }

  const primarySub = getPrimarySub(data?.organization);

  return {
    loading,
    error,
    privileges: rawPrivilegesToAccountPrivileges(primarySub?.privileges),
    refetch,
  };
}

// TODO(hhuang): Replace all uses of this with the useAccountPrivileges hook
// by moving all invocations of this to also occur during hooks.
export async function getAccountPrivileges(
  account: Account
): Promise<Privileges> {
  if (account.accountType === AccountType.Personal) {
    try {
      const response = await client.query<ViewerUserInfoQuery>({
        query: VIEWER_USERINFO_QUERY,
      });

      const userInfoData = response.data.viewer?.userInfo;
      if (userInfoData !== null) {
        const personalEntityStorageLimit =
          userInfoData?.personalEntityStorageLimit ?? 100;

        const privileges: Privileges = {
          ...DEFAULT_PRIVILEGES,
          storageLimitGB: personalEntityStorageLimit,
        };

        return privileges;
      }
      return DEFAULT_PRIVILEGES;
    } catch (error) {
      console.error('There was an error fetching the userinfo JSON: ', error);
      throw error;
    }
  }
  try {
    const response = await client.query<AccountPrivilegesQuery>({
      query: ACCOUNT_PRIVILEGES,
      variables: {organizationId: account.id ?? ''},
    });

    if (response.data?.organization == null) {
      return DEFAULT_PRIVILEGES;
    }
    const primarySub = getPrimarySub(response.data?.organization);
    return rawPrivilegesToAccountPrivileges(primarySub?.privileges);
  } catch (error) {
    console.error('There was an error fetching the data: ', error);
    throw error;
  }
}

// TODO(hhuang): We need to rework our banner framework to no longer require
// async functions, once we do so, remove this and use the hook instead.
export async function getAccountWeaveLimit(
  account: Account
): Promise<WeaveLimits | null> {
  if (account.isEnterprise) {
    const response = await client.query<AccountEnterpriseWeaveLimitsQuery>({
      query: ACCOUNT_ENTERPRISE_WEAVE_LIMITS,
      variables: {organizationId: account.id ?? ''},
    });
    const enterpriseSubscription =
      response.data.organization?.weaveEnterpriseSubscription;
    if (enterpriseSubscription != null) {
      if (enterpriseSubscription.weaveLimit == null) {
        return null;
      }
      return weaveLimitRawToWeaveLimit(enterpriseSubscription.weaveLimit);
    }
    // fallback to priv if no enterprise subscription
  }
  const privileges = await getAccountPrivileges(account);
  return privileges.weaveLimits ?? null;
}

export function useAccountWeaveLimit(account: Account | null): {
  loading: boolean;
  weaveLimit: WeaveLimits | null;
} {
  const skip =
    account == null || !account.isEnterprise || envIsServerDeployment;

  const accountEnterpriseWeaveLimitData = useAccountEnterpriseWeaveLimitsQuery({
    variables: {organizationId: account?.id ?? ''},
    skip,
  });
  const accountPrivilegesData = useAccountPrivileges(account);

  const loading =
    accountEnterpriseWeaveLimitData.loading || accountPrivilegesData.loading;

  if (loading) {
    return {loading, weaveLimit: null};
  }

  if (
    !skip &&
    accountEnterpriseWeaveLimitData.data?.organization
      ?.weaveEnterpriseSubscription != null
  ) {
    const enterpriseSubscription =
      accountEnterpriseWeaveLimitData.data?.organization
        ?.weaveEnterpriseSubscription;
    if (enterpriseSubscription?.weaveLimit == null) {
      return {loading, weaveLimit: null};
    }
    return {
      loading,
      weaveLimit: weaveLimitRawToWeaveLimit(enterpriseSubscription.weaveLimit),
    };
  }

  return {
    loading,
    weaveLimit: accountPrivilegesData.privileges.weaveLimits ?? null,
  };
}

export function useAccountInferenceLimit(account: Account | null): {
  loading: boolean;
  inferenceLimits: InferenceLimits;
} {
  // TODO(hhuang): implement this once this is in orb
  // const skip = account == null || !account.isEnterprise || envIsServerDeployment;

  // const accountEnterpriseWeaveLimitData = useAccountEnterpriseWeaveLimitsQuery({
  //   variables: {organizationId: account?.id ?? ''},
  //   skip,
  // });
  const accountPrivilegesData = useAccountPrivileges(account);

  const loading = accountPrivilegesData.loading;

  if (loading) {
    return {
      loading,
      inferenceLimits: accountPrivilegesData.privileges.inferenceLimits,
    };
  }

  return {
    loading,
    inferenceLimits: accountPrivilegesData.privileges.inferenceLimits,
  };
}

function weaveLimitRawToWeaveLimit(weaveLimit: WeaveLimitsRaw): WeaveLimits {
  return {
    weaveLimitBytes: weaveLimit.weaveLimitBytes,
    weaveOverageCostCents: weaveLimit.weaveOverageCostCents ?? 0,
    weaveOverageUnit: weaveLimit.weaveOverageUnit ?? StorageUnit.Gb,
  };
}

export function getPlanPrivileges(
  plan: Pick<Plan, 'defaultPrivileges'>
): Privileges {
  return rawPrivilegesToAccountPrivileges(plan.defaultPrivileges);
}

export function isAccountMatchingOrg(
  account: Account,
  org?: Pick<Organization, 'id' | 'name'> | null
) {
  return (
    account.name === org?.name ||
    (account.personalOrgId != null && account.personalOrgId === org?.id)
  );
}

export function isPersonalOrg({
  orgType,
}: DeepPartial<Pick<Organization, 'orgType'>>): boolean {
  return orgType === OrgType.Personal;
}

export function isAcademicOrg(
  org: DeepPartial<Pick<Organization, 'subscriptions'>>
): boolean {
  return orgHasSubscriptionType(org, OrganizationSubscriptionType.Academic);
}

export function isAcademicTrialOrg(
  org: DeepPartial<Pick<Organization, 'subscriptions'>>
): boolean {
  return orgHasSubscriptionType(
    org,
    OrganizationSubscriptionType.AcademicTrial
  );
}

export function isNonAcademicOrg(
  org: DeepPartial<Pick<Organization, 'subscriptions'>>
): boolean {
  return !isAcademicOrg(org) && !isAcademicTrialOrg(org);
}

export function isNonPersonalAcademicOrg(
  org: DeepPartial<Pick<Organization, 'orgType' | 'subscriptions'>>
): boolean {
  return !isPersonalOrg(org) && isAcademicOrg(org);
}

export function isTrialOrg<
  T extends HasStripeBillingInfoWithStatus & HasSubsWithSubType,
>(org: T): boolean {
  return (
    org.stripeBillingInfo?.status === 'trialing' ||
    orgHasOneOfSubscriptionTypes(org, [
      OrganizationSubscriptionType.ManualTrial,
      OrganizationSubscriptionType.UserLedTrial,
      OrganizationSubscriptionType.AcademicTrial,
    ])
  );
}

export function isNonPersonalTrialOrg<
  T extends HasOrgType & HasStripeBillingInfoWithStatus & HasSubsWithSubType,
>(org: T): boolean {
  return !isPersonalOrg(org) && isTrialOrg(org);
}

export function isNonPersonalPaidOrg<
  T extends HasOrgType & HasStripeBillingInfoWithStatus & HasSubsWithSubType,
>(org: T): boolean {
  return (
    org.orgType === OrgType.Organization &&
    !isNonPersonalAcademicOrg(org) &&
    !isNonPersonalTrialOrg(org)
  );
}

export function isNonPersonalNonAcademicOrg<
  T extends HasOrgType & HasSubsWithSubType,
>(org: T): boolean {
  return !isPersonalOrg(org) && isNonAcademicOrg(org);
}

export function orgHasSubscriptionType(
  org: DeepPartial<Pick<Organization, 'subscriptions'>>,
  subscriptionType: OrganizationSubscriptionType
): boolean {
  return orgHasOneOfSubscriptionTypes(org, [subscriptionType]);
}

export function orgHasOneOfSubscriptionTypes(
  {subscriptions}: DeepPartial<Pick<Organization, 'subscriptions'>>,
  subscriptionTypes: OrganizationSubscriptionType[]
): boolean {
  return (subscriptionTypes as string[]).includes(
    subscriptions?.[0]?.subscriptionType ?? ''
  );
}

export function prettifyPlanName(name: string): string {
  return _.capitalize(name.replace(/_/g, ' '));
}

async function tryAsyncWithAnalyticsErrorHandling<T, V>({
  asyncClosure,
  transformResultToOutput,
  onNullOutput,
  onError,
}: {
  asyncClosure: () => Promise<T>;
  transformResultToOutput: (raw: T) => Maybe<V>;
  onNullOutput: (raw: T) => void;
  onError: (err: unknown) => void;
}): Promise<Maybe<V>> {
  try {
    const result = await asyncClosure();
    const output = transformResultToOutput(result);
    if (output == null) {
      onNullOutput(result);
    }
    return output;
  } catch (err) {
    onError(err);
    return null;
  }
}

export async function trySubscriptionUpdateWithAnalyticsErrorHandling<U, T, V>({
  mutation,
  input,
  transformDataToOutput,
  subscriptionTypeName,
}: {
  mutation: (rawInput: U) => Promise<ExecutionResult<T>>;
  input: U;
  transformDataToOutput: (data: T) => Maybe<V>;
  subscriptionTypeName: string;
}): Promise<Maybe<V>> {
  const transformResultToOutput = (result: ExecutionResult<T>) => {
    if (result.errors != null || result.data == null) {
      return null;
    }
    return transformDataToOutput(result.data);
  };

  const analyticsEvent = `Upgrade Subscription Error`;
  const errorType = `creating ${subscriptionTypeName} subscription`;

  const onNullOutput = (result: ExecutionResult<T>) => {
    const op = `handle${_.capitalize(subscriptionTypeName)}Subscribe-unknown`;
    const err =
      "Unknown error confirming subscription. Exception didn't throw but success was not reported.";
    const userFacingErrorMsg =
      'Error confirming payment. Please wait a few moments or contact support@wandb.com for help.';
    captureSubscriptionError({
      analyticsEvent,
      err,
      op,
      userFacingErrorMsg,
      extra: {
        errorType,
        reason: result.errors,
      },
    });
  };
  const onError = (err: unknown) => {
    const errMsg = extractErrorMessageFromApolloError(err);
    const userFacingErrorMsg = `Error processing storage subscription payment: ${errMsg}`;
    const op = `create${_.capitalize(subscriptionTypeName)}SubResult`;
    captureSubscriptionError({
      analyticsEvent,
      err,
      op,
      userFacingErrorMsg,
      extra: {
        errorType,
        reason: errMsg,
      },
    });
  };
  return tryAsyncWithAnalyticsErrorHandling({
    asyncClosure: () => mutation(input),
    transformResultToOutput,
    onNullOutput,
    onError,
  });
}

export async function confirmPayment(
  secret: string,
  stripe: Stripe,
  elements: StripeElements,
  subscriptionTypeName: string,
  setErrMsg: (errMsg: string) => void
): Promise<boolean> {
  const op = `handleCardPayment${_.capitalize(subscriptionTypeName)}`;
  const analyticsEvent = `Upgrade Subscription Error`;
  const errorType = `confirming card payment`;
  try {
    const cardElement = elements.getElement(CardElement);
    if (cardElement == null) {
      throw new Error('Stripe card element no loaded.');
    }

    const result = await stripe.confirmCardPayment(secret);

    if (result.error != null) {
      const userFacingErrorMsg = `We upgraded your subscription, but had trouble confirming your payment: ${result.error.message}`;
      captureSubscriptionError({
        err: result.error.message!,
        op,
        analyticsEvent,
        extra: {errorType},
        userFacingErrorMsg,
        setUserFacingErrorMsg: setErrMsg,
      });
      return false;
    }

    return true;
  } catch (err) {
    const userFacingErrorMsg = `We upgraded your subscription, but had trouble confirming your payment: ${err}`;
    captureSubscriptionError({
      err,
      op,
      analyticsEvent,
      extra: {errorType},
      userFacingErrorMsg,
      setUserFacingErrorMsg: setErrMsg,
    });
    return false;
  }
}

type ErrOpts = {
  err: unknown;
  op: string;
  userFacingErrorMsg: string;
  analyticsEvent?: string;
  extra?: {[key: string]: any};
  setUserFacingErrorMsg?: (msg: string) => void;
};
function captureSubscriptionError(opts: ErrOpts) {
  const {err, op, userFacingErrorMsg, analyticsEvent, setUserFacingErrorMsg} =
    opts;
  if (!(err instanceof Error) && typeof err !== 'string') {
    return;
  }

  const extra = {
    location: 'new upgrade subscription form',
    ...opts.extra,
  };
  captureError(err, `NewUpgradeSubscriptionForm-${op}`, {
    extra,
  });
  if (analyticsEvent != null) {
    window.analytics?.track(analyticsEvent, extra);
  }
  if (setUserFacingErrorMsg != null) {
    setUserFacingErrorMsg(userFacingErrorMsg);
  }
}

export function getViewerIsPaid(viewer: Viewer | undefined): boolean {
  return viewer?.organizations.some(isNonPersonalNonAcademicOrg) ?? false;
}

export function useViewerIsPaid(): boolean {
  const viewer = useViewer();
  return getViewerIsPaid(viewer);
}

export function usePrimarySubscriptionPlan(account: Account | null) {
  const skip =
    account == null ||
    account.accountType === AccountType.Personal ||
    envIsServerDeployment;

  const {loading, data} = useAccountSubscriptionPlansQuery({
    variables: {organizationId: account?.id ?? ''},
    skip,
  });

  if (skip || loading || data?.organization == null) {
    return {
      loading,
    };
  }

  const primarySub = getPrimarySub(data?.organization);
  const plan = primarySub?.plan;
  return {
    loading,
    plan: plan,
  };
}
